import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import fuzzyFilterFactory from 'react-fuzzy-filter';
import { CSSTransitionGroup } from 'react-transition-group';
import { formatDate } from '../../helpers';
import PaginatedList from '../../components/PaginatedList';
import ListFilterBox from '../../components/ListFilterBox';
import SelectDropdown from '../../components/SelectDropdown';
import LoadingSpinner from '../../components/LoadingSpinner';

const ControlHeader = props => (
  <Row className="tools">
    <Col sm={4} className="table-header">
      <ListFilterBox
        onChange={props.filterBoxChange}
        fuzzyFilterFactory={props.fuzzyFilterFactory}
      />
    </Col>

    <Col sm={3} smOffset={5} className="table-header">
      <Button
        bsStyle="primary"
        type="submit"
        className="relative-padding"
        onClick={props.addButtonClick}
      >
        Add Organisation
      </Button>
    </Col>
  </Row>
);

const TableHeader = () => (
  <div>
    <Row>
      <Col sm={4} className="table-header">
        Organisation name
      </Col>
      <Col sm={3} className="table-header">
        Date created
      </Col>
      <Col sm={3} className="table-header">
        Date updated
      </Col>
      <Col sm={2} className="table-header">
        Action
      </Col>
    </Row>
    <hr />
  </div>
);

const AddNewRow = props => (
  <Row>
    <Col sm={4} className="table-entry">
      <SelectDropdown
        onChange={props.selectOnChange}
        value={props.selectValue}
        options={props.items}
        aria-label="Organisation Select Box"
        focus={true}
      />
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        className="relative-padding"
        bsStyle="default"
        onClick={props.addItem}
      >
        Add
      </Button>
    </Col>
    <Col sm={2} className="table-entry">
      <Button
        bsStyle="link"
        className="relative-padding"
        onClick={props.cancel}
      >
        Cancel
      </Button>
    </Col>
  </Row>
);

const LineItem = props => {
  const organisation = props.item;
  return (
    <Row key={organisation.id}>
      <Col sm={4} className="table-entry">
        {organisation.name}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(organisation.dateCreated, true)}
      </Col>
      <Col sm={3} className="table-entry">
        {formatDate(organisation.dateUpdated, true)}
      </Col>
      <Col sm={2} className="table-entry">
        <Button
          className="action-button"
          bsStyle="link"
          onClick={() => props.removeItem(organisation)}
          aria-label={`Remove ${organisation.name}`}
        >
          Remove
        </Button>
      </Col>
    </Row>
  );
};

class InsightSetOrganisations extends React.Component {
  static displayName = 'InsightSetOrganisations';

  constructor(props) {
    super(props);
    this.state = {
      newOrgId: undefined,
      showNewOrg: false,
      filterValue: '',
      loading: true
    };
    this.fuzzyFilterFactory = fuzzyFilterFactory();
    this.timeoutId = undefined;
  }

  componentDidMount() {
    // make API call to get organisations for this insightSet
    const insightSetId = parseInt(this.props.params.insightSetId, 10);
    this.getOrganisations(insightSetId);
    this.screenReaderContent.focus();
  }

  componentWillUnmount() {
    // clear timeout to prevent setState on unmounted component
    clearTimeout(this.timeoutId);
  }

  getOrganisations = insightSetId => {
    Promise.all([
      this.props.getInsightSetOrganisations(insightSetId, true),
      this.props.getInsightSetOrganisations(insightSetId, false)
    ]).then(() => this.setState({ loading: false }));
  };

  removeOrganisation = organisation => {
    this.setState({
      isSaving: true,
      errorMsg: undefined,
      successMsg: undefined
    });

    const insightSetId = parseInt(this.props.params.insightSetId, 10);
    this.props
      .removeOrganisationFromInsightSet(insightSetId, organisation)
      .then(response => this.success(response), error => this.failure(error));
  };

  addOrganisation = () => {
    if (this.state.newOrgId) {
      this.setState({
        isSaving: true,
        errorMsg: undefined,
        successMsg: undefined
      });

      const insightSetId = parseInt(this.props.params.insightSetId, 10);
      const newOrgId = this.state.newOrgId;
      this.props
        .addOrganisationToInsightSet(insightSetId, newOrgId)
        .then(response => this.success(response), error => this.failure(error));
    }
    this.setState({ showNewOrg: false, newOrgId: undefined });
  };

  success = response => {
    this.setState({
      isSaving: false,
      errorMsg: undefined,
      successMsg: 'Success!'
    });
    this.timeoutId = setTimeout(
      () => this.setState({ successMsg: undefined }),
      3000
    );
  };

  failure = error => {
    this.setState({ isSaving: false, errorMsg: error.data.message });
  };

  addButtonClick = () => {
    this.setState({ showNewOrg: !this.state.showNewOrg });
  };

  updateFilterValue = filterValue => {
    this.setState({ filterValue: filterValue || '' });
  };

  addButtonClick = () => {
    this.setState({ showNewOrg: !this.state.showNewOrg });
  };

  cancel = () => {
    this.setState({ showNewOrg: false, newOrgId: undefined });
  };

  selectOnChange = newOrgId => {
    this.setState({ newOrgId });
  };

  render() {
    const {
      allocated,
      unallocated
    } = this.props.currentInsightSet.organisations;

    const LineItemWrapper = props => (
      <LineItem {...props} removeItem={this.removeOrganisation} />
    );

    return (
      <div>
        <Row />
        <h3
          className="screen-reader-only"
          tabIndex="-1"
          ref={i => (this.screenReaderContent = i)}
        >
          Showing organisations with access to insight set{' '}
          {this.props.currentInsightSet.name}
        </h3>

        <ControlHeader
          filterBoxChange={this.updateFilterValue}
          fuzzyFilterFactory={this.fuzzyFilterFactory}
          addButtonClick={this.addButtonClick}
        />

        <TableHeader />

        {this.state.showNewOrg && (
          <AddNewRow
            items={unallocated}
            addItem={this.addOrganisation}
            selectValue={this.state.newOrgId}
            selectOnChange={this.selectOnChange}
            cancel={this.cancel}
          />
        )}

        {this.state.loading && <LoadingSpinner />}

        {!this.state.loading && (
          <PaginatedList
            filterValue={this.state.filterValue}
            component={LineItemWrapper}
            items={allocated}
            fuzzyFilterFactory={this.fuzzyFilterFactory}
            pluralName="organisations"
            tableHeader={TableHeader}
          />
        )}
        <Row>
          <Col sm={12}>
            <CSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={300}
              transitionLeaveTimeout={300}
            >
              {this.state.successMsg && (
                <p className="text-success">{this.state.successMsg}</p>
              )}
            </CSSTransitionGroup>
          </Col>
        </Row>
        {this.state.errorMsg && (
          <div className="alert alert-danger">{this.state.errorMsg}</div>
        )}
      </div>
    );
  }
}

export default InsightSetOrganisations;



// WEBPACK FOOTER //
// ./src/containers/insightset/InsightSetOrganisations.js